// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions are visual labels for the adjacent control (same convention as SupplierAdmin / the UserAdmin* lists, Task #25).
//
// =============================================================================
// SupplierFormModal — New / Edit supplier as an Unleashed-style modal
// =============================================================================
// Shared modal for BOTH creating a Zephyr-owned supplier and editing an existing
// row's Zephyr overlay + billing model (db-50). Layout follows the CLAUDE.md
// "Form / field layout" convention: two columns, right-aligned labels, fixed-width
// inputs sized to the data (not stretched), uniform width within a column. Cancel/
// Save sit bottom-left (app convention). Reads/writes go through src/data/suppliers.
//
// Stage 2 is ADDITIVE: the existing controls (Type/kind with all three options,
// the Print-Authorisation toggle, the overlay fields) are unchanged; the new
// db-50 fields (payment_method, document_type, action_sheet_override, dropdown_label)
// are added alongside. No wizard / Action Sheet behaviour changes here — those
// move onto the new fields in Stages 3-4.
// =============================================================================
import React from "react";
import { getSupplierLogoUrl, uploadSupplierLogo } from "../../data/storage";
import { createSupplier, updateSupplierById } from "../../data/suppliers";
import { listProducts } from "../../data/unleashedProducts";
import { deriveSupplierType } from "../../services/approvals";
import { alertDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

// Order-capture flow (suppliers.kind). Since db-50b narrowed kind, "reimbursement"
// is a payment_method (not a capture kind) — the two capture flows are standard vs
// School Locker.
const KIND_OPTIONS = [
  { value: "standard", label: "Standard (normal uniform order)" },
  { value: "school_locker", label: "School Locker (electronic order sheet)" },
];

// How Zephyr pays (axis 1).
const PAYMENT_OPTIONS = [
  { value: "supplier_invoices", label: "Supplier invoices Zephyr" },
  { value: "shelter_reimbursed", label: "DFV Service pays, Zephyr reimburses" },
];

// The client's document (axis 2).
const DOCUMENT_OPTIONS = [
  { value: "authorisation_to_purchase", label: "Authorisation to Purchase" },
  { value: "reimbursement_approval", label: "Approval for Reimbursement" },
  { value: "approved_list_only", label: "Approved list only" },
  { value: "school_locker_order", label: "School Locker order sheet" },
  { value: "none", label: "None" },
];

// Action Sheet next-steps type (axis 3). No "auto" — the office must actively pick a
// concrete type (blank "— Select —" default, Save blocked until chosen); a "Suggested
// from billing" hint guides the choice. deriveSupplierType honours it as an override.
const ACTION_SHEET_OPTIONS = [
  { value: "", label: "— Select —" },
  { value: "authority", label: "Authority (Print Authorisation)" },
  { value: "direct", label: "Direct (supplier invoices Zephyr)" },
  { value: "reimbursement", label: "Reimbursement (DFV Service claims back)" },
  { value: "school_locker", label: "School Locker" },
];

// Short labels for the "Suggested from billing" hint under the Action Sheet picker.
const SHORT_LABEL = {
  authority: "Authority",
  direct: "Direct",
  reimbursement: "Reimbursement",
  school_locker: "School Locker",
};

// Compact, fixed-width field styling (sized to data, not stretched).
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid #CFD8DC",
  fontSize: 13,
  color: "#1A2E2B",
  background: "#fff",
  fontFamily: "inherit",
};

// One label + control row. Right-aligned fixed-width label + fixed-width control
// cell so every control in a column lines up and is the same width (rules 4/5).
function Row({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "126px 218px",
        gap: 8,
        alignItems: "center",
        marginBottom: 6,
      }}
    >
      <label style={{ textAlign: "right", fontSize: 12, color: "#5A6B68" }}>{label}</label>
      <div>{children}</div>
    </div>
  );
}

// Full-width variant (label + a control that fills the modal width) for fields that
// need the room — Footer Message + Logo.
function WideRow({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "126px 1fr",
        gap: 8,
        alignItems: "start",
        marginBottom: 6,
      }}
    >
      <label style={{ textAlign: "right", fontSize: 12, color: "#5A6B68", paddingTop: 4 }}>
        {label}
      </label>
      <div>{children}</div>
    </div>
  );
}

function Sel({ value, onChange, options, ariaLabel }) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Txt({ value, onChange, placeholder, maxLength, width }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      style={width ? { ...inputStyle, width } : inputStyle}
    />
  );
}

// Which authorisation-PDF layout this supplier prints (…15). "supplier_account" is
// the current form (default). See uniformAuthorityPdf.ts.
const DOC_TEMPLATE_OPTIONS = [
  {
    value: "supplier_account",
    label: "Supplier account — current form (bearer orders on Zephyr's 30-day account)",
  },
  {
    value: "invoice_zephyr",
    label: "Invoice Zephyr — school / other (they email Zephyr a tax invoice)",
  },
  {
    value: "reimbursement",
    label: "Reimbursement — DFV Service to Buy (Zephyr reimburses the service)",
  },
];

function seedForm(mode, row) {
  const r = row || {};
  const kind = r.kind || "standard";
  const payment_method = r.payment_method || "supplier_invoices";
  const document_type = r.document_type || "none";
  return {
    name: mode === "edit" ? r.name || "" : "",
    dropdown_label: r.dropdown_label || "",
    kind,
    so_suffix: r.so_suffix || "",
    is_uniform_supplier: mode === "edit" ? !!r.is_uniform_supplier : true,
    unleashed_product_code: r.unleashed_product_code || "",
    payment_method,
    document_type,
    // Force an active pick: blank unless the row already carries an explicit override.
    action_sheet_override: r.action_sheet_override || "",
    has_authority_doc: !!r.has_authority_doc,
    doc_template: r.doc_template || "supplier_account",
    doc_name: r.doc_name || "",
    zephyr_account_number: r.zephyr_account_number || "",
    account_terms: r.account_terms || "",
    footer_message: r.footer_message || "",
    abn: r.abn || "",
  };
}

// mode: "create" | "edit". row: the supplier row (edit) or null (create).
// onClose(): dismiss. onSaved(): parent refreshes its list.
export function SupplierFormModal({ mode, row, onClose, onSaved }) {
  const [form, setForm] = React.useState(() => seedForm(mode, row));
  const [logoFile, setLogoFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // Active Unleashed product codes — power the Unleashed-code datalist + validate
  // the uniform-supplier gate against the real catalogue.
  const [productCodes, setProductCodes] = React.useState([]);
  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  React.useEffect(() => {
    let cancelled = false;
    listProducts().then((rows) => {
      if (cancelled) return;
      setProductCodes((rows || []).filter((p) => !p.deactivated).map((p) => p.product_code));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showOverlay = !!form.has_authority_doc;
  const supplierCode = mode === "edit" && row ? row.supplier_code : "(auto-generated)";
  // Zephyr-owned specials (ZEPHYR-* codes, e.g. "DFV Service to Buy") are NOT mirrored
  // from Unleashed, so their Name is safe to edit in place. Unleashed-mirrored suppliers
  // keep a read-only Name so it never drifts from the source system.
  const isZephyrOwned =
    mode === "edit" && row && String(row.supplier_code || "").startsWith("ZEPHYR");
  const nameEditable = mode !== "edit" || isZephyrOwned;
  const hasCurrentLogo = mode === "edit" && row && row.logo_path && !logoFile;
  // Billing-derived suggestion (ignores the override) to guide the forced pick.
  const suggestedType = deriveSupplierType({
    kind: form.kind,
    payment_method: form.payment_method,
    document_type: form.document_type,
  });

  async function handleSave() {
    const trimOrNull = (v) => (v || "").trim() || null;
    const name = (form.name || "").trim();
    if (!name) {
      await alertDialog({ title: "Name required", message: "Enter a supplier name." });
      return;
    }
    if (!form.action_sheet_override) {
      await alertDialog({
        title: "Action Sheet type required",
        message: "Choose the Action Sheet next-steps type for this supplier.",
      });
      return;
    }
    // Gate: a uniform supplier must carry a real Unleashed product code, so its
    // uniforms can never reach the Order Builder unmapped (docs/unleashed-interface.md).
    const uCode = (form.unleashed_product_code || "").trim();
    if (form.is_uniform_supplier) {
      if (!uCode) {
        await alertDialog({
          title: "Unleashed product code required",
          message:
            "A uniform supplier needs an Unleashed product code (used on its Sales Order lines). Enter one, or untick “Show in uniform dropdown”.",
        });
        return;
      }
      if (productCodes.length > 0 && !productCodes.includes(uCode)) {
        await alertDialog({
          title: "Unknown product code",
          message: `“${uCode}” isn't in the Unleashed product catalogue. Add it in Manage → Unleashed → Products first, then select it here.`,
        });
        return;
      }
    }
    const patch = {
      is_uniform_supplier: !!form.is_uniform_supplier,
      unleashed_product_code: trimOrNull(form.unleashed_product_code),
      kind: form.kind || "standard",
      so_suffix: trimOrNull(form.so_suffix),
      payment_method: form.payment_method || "supplier_invoices",
      document_type: form.document_type || "none",
      action_sheet_override: form.action_sheet_override,
      dropdown_label: trimOrNull(form.dropdown_label),
      has_authority_doc: !!form.has_authority_doc,
      doc_template: form.doc_template || "supplier_account",
      doc_name: trimOrNull(form.doc_name),
      zephyr_account_number: trimOrNull(form.zephyr_account_number),
      account_terms: trimOrNull(form.account_terms),
      footer_message: trimOrNull(form.footer_message),
      abn: trimOrNull(form.abn),
    };
    // Persist the Name only when it's editable (create, or a Zephyr-owned special) so an
    // Unleashed-mirrored supplier's name is never overwritten from here.
    if (nameEditable) patch.name = name;
    const code = mode === "edit" && row ? row.supplier_code : `ZEPHYR-${Date.now()}`;

    setSaving(true);
    if (logoFile) {
      const ext = (logoFile.name.split(".").pop() || "").toLowerCase();
      if (ext !== "png" && ext !== "jpg" && ext !== "jpeg") {
        setSaving(false);
        await alertDialog({
          title: "Logo not uploaded",
          message: "Please choose a PNG or JPG image.",
        });
        return;
      }
      const path = `${code}-${Date.now()}.${ext}`;
      const { error: upErr } = await uploadSupplierLogo(path, logoFile, {
        upsert: true,
        contentType: logoFile.type || undefined,
      });
      if (upErr) {
        setSaving(false);
        await alertDialog({ title: "Logo upload failed", message: upErr.message });
        return;
      }
      patch.logo_path = path;
    }

    let error;
    if (mode === "edit" && row) {
      ({ error } = await updateSupplierById(row.id, patch));
    } else {
      ({ error } = await createSupplier({
        supplier_code: code,
        name: name,
        deactivated: false,
        ...patch,
      }));
    }
    setSaving(false);
    if (error) {
      await alertDialog({ title: "Save failed", message: error.message });
      return;
    }
    logAudit(mode === "edit" ? "supplier_updated" : "supplier_created", {
      id: mode === "edit" && row ? row.id : undefined,
      supplier_code: code,
      name: name,
      ...patch,
    });
    onSaved();
    onClose();
  }

  return (
    <ModalBackdrop onClose={onClose} portal contentStyle={{ maxWidth: 880 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: "12px 18px" }}>
        <h2
          style={{
            margin: "0 0 8px",
            paddingBottom: 8,
            borderBottom: "1px solid #E0E7E6",
            fontSize: 18,
            color: "#1A2E2B",
          }}
        >
          {mode === "edit" ? "Edit Supplier" : "Add Supplier"}
        </h2>

        {/* Two columns of fixed-width fields (rules 1-5) */}
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          <div>
            <Row label="Supplier Code">
              <div style={{ fontSize: 13, color: "#5A6B68" }}>{supplierCode}</div>
            </Row>
            <Row label="Name">
              {nameEditable ? (
                <Txt
                  value={form.name}
                  onChange={(v) => setF("name", v)}
                  placeholder="Supplier name"
                />
              ) : (
                <div style={{ fontSize: 13, color: "#1A2E2B" }}>{form.name}</div>
              )}
            </Row>
            <Row label="ABN">
              <Txt
                value={form.abn}
                onChange={(v) => setF("abn", v)}
                placeholder="e.g. 40 000 031 714"
              />
            </Row>
            <Row label="Dropdown Label">
              <Txt
                value={form.dropdown_label}
                onChange={(v) => setF("dropdown_label", v)}
                placeholder={form.name || "(uses name)"}
              />
            </Row>
            <Row label="Type">
              <Sel value={form.kind} onChange={(v) => setF("kind", v)} options={KIND_OPTIONS} />
            </Row>
            <Row label="SO Suffix">
              <Txt
                value={form.so_suffix}
                onChange={(v) => setF("so_suffix", v)}
                placeholder="e.g. LOW"
                maxLength={8}
                width={96}
              />
            </Row>
            <Row label="Uniform">
              <label
                style={{
                  fontSize: 13,
                  color: "#1A2E2B",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.is_uniform_supplier}
                  onChange={(e) => setF("is_uniform_supplier", e.target.checked)}
                />
                Show in uniform dropdown
              </label>
            </Row>
            {form.is_uniform_supplier && (
              <Row label="Unleashed Code">
                <div>
                  <input
                    aria-label="Unleashed product code"
                    list="supplier-unleashed-codes"
                    value={form.unleashed_product_code}
                    onChange={(e) => setF("unleashed_product_code", e.target.value)}
                    placeholder="e.g. UNI_LOW"
                    style={inputStyle}
                  />
                  <datalist id="supplier-unleashed-codes">
                    {productCodes.map((code) => (
                      <option key={code} value={code} />
                    ))}
                  </datalist>
                  <div style={{ fontSize: 11, color: "#5A6B68", marginTop: 2 }}>
                    Required — the product for this supplier's uniform order lines.
                  </div>
                </div>
              </Row>
            )}
          </div>

          <div>
            <Row label="How Zephyr pays">
              <Sel
                value={form.payment_method}
                onChange={(v) => setF("payment_method", v)}
                options={PAYMENT_OPTIONS}
              />
            </Row>
            <Row label="Document">
              <Sel
                value={form.document_type}
                onChange={(v) => setF("document_type", v)}
                options={DOCUMENT_OPTIONS}
              />
            </Row>
            <Row label="Action Sheet">
              <div>
                <Sel
                  value={form.action_sheet_override}
                  onChange={(v) => setF("action_sheet_override", v)}
                  options={ACTION_SHEET_OPTIONS}
                  ariaLabel="Action Sheet type"
                />
                <div style={{ fontSize: 11, color: "#5A6B68", marginTop: 2 }}>
                  Suggested from billing: {SHORT_LABEL[suggestedType]}
                </div>
              </div>
            </Row>
            <Row label="Print Auth. doc">
              <label
                style={{
                  fontSize: 13,
                  color: "#1A2E2B",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.has_authority_doc}
                  onChange={(e) => setF("has_authority_doc", e.target.checked)}
                />
                Produces a Print Authorisation
              </label>
            </Row>
          </div>
        </div>

        {/* Overlay fields — only for suppliers whose document prints */}
        {showOverlay && (
          <div style={{ marginTop: 6, borderTop: "1px solid #E0E7E6", paddingTop: 8 }}>
            <div style={{ fontSize: 12, color: "#5A6B68", marginBottom: 6 }}>
              These appear on the printed authorisation document.
            </div>
            <div>
              <Row label="Authorisation form">
                <Sel
                  ariaLabel="Authorisation form"
                  value={form.doc_template}
                  onChange={(v) => setF("doc_template", v)}
                  options={DOC_TEMPLATE_OPTIONS}
                />
              </Row>
              <Row label="Doc Name">
                <Txt value={form.doc_name} onChange={(v) => setF("doc_name", v)} />
              </Row>
              <Row label="Zephyr Acc No.">
                <Txt
                  value={form.zephyr_account_number}
                  onChange={(v) => setF("zephyr_account_number", v)}
                />
              </Row>
              <Row label="Account Terms">
                <Txt value={form.account_terms} onChange={(v) => setF("account_terms", v)} />
              </Row>
            </div>
            <WideRow label="Footer Message">
              <textarea
                value={form.footer_message}
                onChange={(e) => setF("footer_message", e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </WideRow>
            <WideRow label="Logo">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {hasCurrentLogo && (
                  <img
                    src={getSupplierLogoUrl(row.logo_path)}
                    alt={`${form.doc_name || form.name} current logo`}
                    style={{ maxHeight: 30 }}
                  />
                )}
                <input
                  id="supplier-logo-input"
                  type="file"
                  accept="image/png,image/jpeg"
                  aria-label="Logo (PNG/JPG)"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="supplier-logo-input"
                  style={{
                    display: "inline-block",
                    border: "1px solid #90CAF9",
                    background: "#E3F2FD",
                    color: "#0D47A1",
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Choose logo…
                </label>
                <span style={{ fontSize: 11, color: "#5A6B68" }}>
                  {logoFile
                    ? logoFile.name
                    : hasCurrentLogo
                      ? "Current logo (choose to replace)"
                      : "No logo"}
                </span>
              </div>
            </WideRow>
          </div>
        )}

        {/* Cancel / Save bottom-left (app convention) */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Btn variant="secondary" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
